import React, { useState } from 'react';
import {
  Box, Typography, Paper, Stack, Button, Chip, Table,
  TableBody, TableCell, TableHead, TableRow, Tooltip,
  TextField, InputAdornment, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WorkIcon from '@mui/icons-material/Work';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ArticleIcon from '@mui/icons-material/Article';
import { useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import type { PoStatus } from '../data/types';
import { BRAND } from '../theme/tokens';
import { StatusChip } from '../components/StatusChip';

const FI_ORANGE = '#F57C00';
const FI_BLUE   = '#027BFF';

const SUPPLIER_AVATARS: Record<string, string> = {
  JAS: '#2e7d32', APD: '#1565c0', REPCO: '#b71c1c', '247': '#e65100',
};

function PoStatusChip({ status }: { status: PoStatus }) {
  return <StatusChip status={status} />;
}

// Map a PO id to a stable route-friendly key.
// We use the PO id directly (e.g. 'PO-4575') and the print page resolves by id.
function poRouteId(poId: string): string { return poId; }

export const POListPage: React.FC = () => {
  const navigate = useNavigate();
  const { purchaseOrders, suppliers, resetData } = useData();
  const [search, setSearch] = useState('');

  // New PO dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newJobNum, setNewJobNum] = useState('');
  const [newSupplier, setNewSupplier] = useState('sup2');
  const [raised, setRaised] = useState(false);

  // Reset data dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  const handleReset = () => {
    resetData();
    setResetDone(true);
    setTimeout(() => { setResetDialogOpen(false); setResetDone(false); }, 1200);
  };

  const nextPoNumber = () => {
    const nums = purchaseOrders
      .map(p => parseInt(p.id.replace('PO-', ''), 10))
      .filter(n => !isNaN(n));
    const next = nums.length > 0 ? Math.max(...nums) + 1 : 4600;
    return `PO-${next}`;
  };

  const handleOpenDialog = () => {
    setNewJobNum('');
    setNewSupplier('sup2');
    setRaised(false);
    setDialogOpen(true);
  };

  const handleRaisePO = () => {
    setRaised(true);
    setTimeout(() => setDialogOpen(false), 1400);
  };

  const filtered = purchaseOrders.filter(po => {
    const sup = suppliers.find(s => s.id === po.supplierId);
    const q = search.toLowerCase();
    return (
      po.id.toLowerCase().includes(q) ||
      (sup?.name ?? '').toLowerCase().includes(q) ||
      po.jobRef.toLowerCase().includes(q)
    );
  });

  const totalValue = purchaseOrders.reduce((s, p) => s + p.total, 0);
  const drafted  = purchaseOrders.filter(p => ['Draft', 'Submitted', 'Approved'].includes(p.status)).length;
  const pending  = purchaseOrders.filter(p => ['Partial received', 'Bill received'].includes(p.status)).length;
  const complete = purchaseOrders.filter(p => ['All received', 'Finalised', 'Bill received'].includes(p.status)).length;

  const newPoNumber = nextPoNumber();

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: BRAND }}>Purchase Orders</Typography>
          <Typography variant="body2" color="text.secondary">Northbridge Diesel & Mechanical</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Reset all POs, Bills and line items back to baseline test data">
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              size="small"
              onClick={() => setResetDialogOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 700, borderColor: '#ccc', color: '#888',
                '&:hover': { borderColor: '#e53935', color: '#e53935', backgroundColor: '#fff5f5' } }}
            >
              Reset Data
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<ArticleIcon sx={{ fontSize: 15 }} />}
            size="small"
            onClick={() => navigate('/statement-import')}
            sx={{ textTransform: 'none', fontWeight: 700, borderColor: BRAND, color: BRAND,
              '&:hover': { backgroundColor: '#f5f0ff' } }}
          >
            Statement Import
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            onClick={handleOpenDialog}
            sx={{ backgroundColor: FI_ORANGE, textTransform: 'none', fontWeight: 700 }}
          >
            New Purchase Order
          </Button>
        </Stack>
      </Stack>

      {/* Reset confirmation dialog */}
      <Dialog open={resetDialogOpen} onClose={() => !resetDone && setResetDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 0.5 } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', color: resetDone ? '#2e7d32' : '#c62828', pb: 0.5 }}>
          {resetDone ? '✓ Data reset successfully' : 'Reset to baseline test data?'}
        </DialogTitle>
        <DialogContent>
          {resetDone ? (
            <Typography sx={{ fontSize: '0.82rem', color: '#555' }}>
              All POs, Bills and line items have been restored to the original 12 test scenarios.
            </Typography>
          ) : (
            <Typography sx={{ fontSize: '0.82rem', color: '#555', lineHeight: 1.6 }}>
              This will clear all changes and restore the <strong>12 baseline test scenarios</strong> — including all POs, Bills, suppliers and line items.
              <br /><br />
              Any POs or bills you've created or modified will be lost.
            </Typography>
          )}
        </DialogContent>
        {!resetDone && (
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={() => setResetDialogOpen(false)} variant="outlined" size="small"
              sx={{ textTransform: 'none', fontWeight: 700, borderColor: '#ccc', color: '#666' }}>
              Cancel
            </Button>
            <Button onClick={handleReset} variant="contained" size="small" startIcon={<RestartAltIcon />}
              sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: '#e53935',
                '&:hover': { backgroundColor: '#b71c1c' } }}>
              Reset All Data
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Summary cards */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
        {[
          { label: 'Total PO Value', value: `$${totalValue.toLocaleString('en-AU', { minimumFractionDigits: 2 })}`, color: BRAND, bg: '#f5f0ff' },
          { label: 'Draft',          value: drafted,  color: '#666',    bg: '#f5f5f5' },
          { label: 'In Progress',    value: pending,  color: '#e65100', bg: '#fff3e0' },
          { label: 'Complete',       value: complete, color: '#1b5e20', bg: '#e8f5e9' },
        ].map(card => (
          <Paper key={card.label} sx={{ flex: 1, p: 1.5, border: `1px solid ${card.color}30`, backgroundColor: card.bg }}>
            <Typography variant="h5" fontWeight={800} sx={{ color: card.color }}>{card.value}</Typography>
            <Typography variant="caption" sx={{ color: card.color, fontWeight: 600 }}>{card.label}</Typography>
          </Paper>
        ))}
      </Stack>

      {/* Search */}
      <Box mb={2}>
        <TextField size="small" placeholder="Search POs, suppliers, JOB #..."
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 340 }} />
      </Box>

      {/* Table */}
      <Paper sx={{ border: '1px solid #e0e0e0', overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f0ff' }}>
              {['PO Number', 'Date', 'Supplier', 'Job', 'Lines', 'Ex GST', 'GST', 'Total', 'Status', ''].map(h => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', color: BRAND }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(po => {
              const sup = suppliers.find(s => s.id === po.supplierId);
              return (
                <TableRow
                  key={po.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/po-print/${poRouteId(po.id)}`)}
                >
                  <TableCell>
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      <Typography variant="caption" sx={{ color: FI_ORANGE, fontWeight: 700 }}>{po.id}</Typography>
                      {po.lines === 0 && (
                        <Chip label="0 items" size="small"
                          sx={{ fontSize: '0.58rem', height: 15, fontWeight: 700,
                            backgroundColor: '#fff3e0', color: FI_ORANGE, border: `1px solid ${FI_ORANGE}40` }} />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>
                    {new Date(po.date).toLocaleDateString('en-AU')}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      <Avatar sx={{ width: 22, height: 22, fontSize: '0.55rem', fontWeight: 800,
                        backgroundColor: SUPPLIER_AVATARS[sup?.code ?? ''] || '#555' }}>
                        {(sup?.code ?? '??').slice(0, 3)}
                      </Avatar>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', maxWidth: 160 }} noWrap>
                        {sup?.name ?? '—'}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {po.jobRef ? (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <WorkIcon sx={{ fontSize: 13, color: FI_ORANGE }} />
                        <Typography sx={{ fontSize: '0.75rem', color: FI_BLUE, fontWeight: 600 }}>{po.jobRef}</Typography>
                      </Stack>
                    ) : (
                      <Typography sx={{ fontSize: '0.72rem', color: '#bbb' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: po.lines === 0 ? 700 : 400, color: po.lines === 0 ? FI_ORANGE : '#333' }}>
                      {po.lines}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>${po.exGst.toFixed(2)}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>${po.gst.toFixed(2)}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                    ${po.total.toFixed(2)}
                  </TableCell>
                  <TableCell><PoStatusChip status={po.status} /></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Print Preview">
                        <Button size="small" variant="outlined" startIcon={<PrintIcon />}
                          sx={{ textTransform: 'none', fontSize: '0.68rem', borderColor: BRAND, color: BRAND }}
                          onClick={e => { e.stopPropagation(); navigate(`/po-print/${poRouteId(po.id)}`); }}>
                          Print
                        </Button>
                      </Tooltip>
                      {(['All received', 'Submitted', 'Approved', 'Partial received'] as PoStatus[]).includes(po.status) && (
                        <Tooltip title="Generate Bill from this PO">
                          <Button size="small" variant="contained" startIcon={<ReceiptLongIcon sx={{ fontSize: 11 }} />}
                            sx={{ textTransform: 'none', fontSize: '0.68rem', backgroundColor: FI_ORANGE, minWidth: 'unset',
                              '&:hover': { backgroundColor: '#e65100' } }}
                            onClick={e => { e.stopPropagation(); navigate('/bills'); }}>
                            Bill
                          </Button>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Box sx={{ px: 2, py: 1, borderTop: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}>
          <Typography variant="caption" color="text.secondary">{filtered.length} purchase orders</Typography>
        </Box>
      </Paper>

      {/* Raise New PO Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>Raise New Purchase Order</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#888', mt: 0.2 }}>
            Creates a PO number immediately with 0 line items.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 1.5 }}>
          <Box sx={{ p: 1.5, mb: 2, borderRadius: 2, backgroundColor: '#f5f0ff', border: `1px solid ${BRAND}33` }}>
            <Typography sx={{ fontSize: '0.72rem', color: BRAND, fontWeight: 700, mb: 0.3 }}>How this works</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#555', lineHeight: 1.5 }}>
              This raises <strong>{newPoNumber}</strong> with 0 items — you get the PO number straight away to reference on the job.
              When the supplier invoice arrives, upload it to the linked Bill and <strong>backfill the line items</strong> back to this PO.
            </Typography>
          </Box>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Supplier</InputLabel>
            <Select value={newSupplier} label="Supplier" onChange={e => setNewSupplier(e.target.value)}>
              {suppliers.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth size="small" label="Job Number" placeholder="e.g. 234"
            value={newJobNum} onChange={e => setNewJobNum(e.target.value)}
            helperText={newJobNum.trim() ? `Will save as JOB #${newJobNum.trim().replace(/[^0-9]/g, '')}` : 'Enter the job number this PO is for'}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <WorkIcon sx={{ fontSize: 16, color: FI_ORANGE }} />
                  <Typography sx={{ fontSize: '0.8rem', color: '#888', ml: 0.5 }}>JOB #</Typography>
                </InputAdornment>
              ),
            }}
          />

          {raised && (
            <Alert severity="success" sx={{ mt: 2, fontSize: '0.75rem' }}>
              {newPoNumber} raised — 0 items, Draft status.
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none', color: '#888' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRaisePO}
            disabled={raised}
            sx={{ backgroundColor: BRAND, textTransform: 'none', fontWeight: 700, px: 3,
              '&:hover': { backgroundColor: '#4a3580' } }}
          >
            {raised ? `✓ ${newPoNumber} raised` : `Raise ${newPoNumber}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
